import { Alert, Button, Textarea, cn } from '@blink/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, Check, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ErrorAlert } from '@/components/common';
import { currentLocation } from '@/components/route-bits';
import { useI18n } from '@/i18n';
import { api, type ManualAwbStatus } from '@/lib/api';
import { compressPhoto } from '@/lib/photo';

/**
 * Damaged label (plan 4.5 screen 7): the courier never types the AWB. The parcel is the one on
 * screen; the courier picks the piece, takes a photo of the label and of the parcel, and a team
 * leader or hub manager approves. Approved: the piece counts as scanned.
 */
export function ManualAwb({ shipmentId, pieces, manual }: { shipmentId: string; pieces: string[]; manual: ManualAwbStatus | null }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [piece, setPiece] = useState<string | null>(pieces.length === 1 ? pieces[0]! : null);
  const [label, setLabel] = useState<File | null>(null);
  const [parcel, setParcel] = useState<File | null>(null);
  const [note, setNote] = useState('');

  const send = useMutation({
    mutationFn: async () => {
      const [labelSmall, parcelSmall, where] = await Promise.all([compressPhoto(label!), compressPhoto(parcel!), currentLocation(4000)]);
      const form = new FormData();
      form.append('shipmentId', shipmentId);
      if (piece) form.append('pieceLabel', piece);
      if (note.trim()) form.append('note', note.trim());
      if (where) {
        form.append('latitude', String(where.latitude));
        form.append('longitude', String(where.longitude));
      }
      form.append('label', labelSmall, 'label.jpg');
      form.append('parcel', parcelSmall, 'parcel.jpg');
      return api<ManualAwbStatus>('/courier/manual-awb', { method: 'POST', body: form });
    },
    onSuccess: () => {
      setOpen(false);
      setLabel(null);
      setParcel(null);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['courier'] });
    },
  });

  if (manual?.status === 'PENDING') {
    return (
      <p className="flex items-center gap-1.5 text-xs font-semibold text-warning-foreground" data-testid="manual-pending">
        <TriangleAlert className="h-4 w-4" />
        {t.manual.pending}
      </p>
    );
  }
  if (!pieces.length) return null;

  return (
    <div className="space-y-2">
      {manual?.status === 'REJECTED' ? (
        <Alert variant="destructive" data-testid="manual-rejected">
          {t.manual.rejected}
          {manual.decisionNote ? ` “${manual.decisionNote}”` : ''}
        </Alert>
      ) : null}
      {!open ? (
        <Button variant="ghost" size="sm" className="h-9 px-2 text-warning-foreground" onClick={() => setOpen(true)} data-testid="manual-open">
          <TriangleAlert className="h-4 w-4" />
          {t.manual.open}
        </Button>
      ) : (
        <div className="space-y-3 rounded-lg border border-warning/40 bg-warning-soft/40 p-3" data-testid="manual-form">
          <p className="text-xs text-muted-foreground">{t.manual.hint}</p>
          {pieces.length > 1 ? (
            <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={t.manual.whichPiece}>
              {pieces.map((p) => (
                <button
                  key={p}
                  type="button"
                  role="radio"
                  aria-checked={piece === p}
                  onClick={() => setPiece(p)}
                  className={cn('rounded-md border px-3 py-1.5 font-mono text-sm', piece === p ? 'border-primary bg-primary text-primary-foreground' : 'bg-card')}
                >
                  {p}
                </button>
              ))}
            </div>
          ) : null}
          <PhotoPick label={t.manual.labelPhoto} file={label} onPick={setLabel} testId="manual-label" />
          <PhotoPick label={t.manual.parcelPhoto} file={parcel} onPick={setParcel} testId="manual-parcel" />
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={t.manual.note} rows={2} maxLength={300} />
          <div className="flex gap-2">
            <Button className="flex-1" disabled={!piece || !label || !parcel || send.isPending} onClick={() => send.mutate()} data-testid="manual-send">
              {send.isPending ? t.deliver.saving : t.manual.send}
            </Button>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t.scan.cancel}
            </Button>
          </div>
          <ErrorAlert error={send.error} />
        </div>
      )}
    </div>
  );
}

function PhotoPick({ label, file, onPick, testId }: { label: string; file: File | null; onPick: (f: File) => void; testId: string }) {
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => {
    if (!file) return setPreview(null);
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return (
    <label className="flex cursor-pointer items-center gap-3 rounded-md border bg-card p-2 text-sm font-semibold">
      {preview ? <img src={preview} alt="" className="h-12 w-12 rounded object-cover" /> : <Camera className="m-3 h-6 w-6 text-muted-foreground" />}
      <span className="flex-1">{label}</span>
      {file ? <Check className="h-5 w-5 text-success" /> : null}
      <input
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        data-testid={testId}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) onPick(f);
        }}
      />
    </label>
  );
}
